import { useState } from "react";
import { Modal, ModalOverlay, ModalContent } from "@chakra-ui/react";
import { translate } from "ui/i18n";
import { useWindowWidth } from "ui/utils/hooks";
import { CloseButton } from "ui/pages/app/components/buttons/CloseButton";
import { PrimaryButton } from "ui/pages/app/components/buttons/PrimaryButton";
import { useAppDispatch } from "ui/hook/store";
import {
  createSeanceTiming,
  updateSeanceTiming,
} from "domain/seances/seances.actions";
import { Snackbar } from "ui/pages/snackbar";
import { HiOutlineX } from "react-icons/hi";
import { INTimeInput } from "../../components/inputs/INTimeInput";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  timing: string;
  seanceId: number;
}

export function ModalManageTiming({
  isOpen,
  onClose,
  timing,
  seanceId,
}: IProps) {
  const isDesktop = useWindowWidth();
  const dispatch = useAppDispatch();
  const [hasClosedAdvice, setHasClosedAdvice] = useState(false);
  const [formattedTiming, setFormattedTiming] = useState(timing);

  const timingModalAdviceClosed =
    localStorage.getItem("closed-timing-modal-advice") === "true";

  const handleValidate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (timing) {
      const result = await dispatch(
        updateSeanceTiming({ seanceId, timing: formattedTiming })
      ).unwrap();
      if (result.success) {
        Snackbar.showSuccess({
          title: translate("ns1:Seance.TimingUpdated"),
        });
        onClose();
      }
    } else {
      const result = await dispatch(
        createSeanceTiming({ seanceId, timing: formattedTiming })
      ).unwrap();
      if (result.success) {
        Snackbar.showSuccess({
          title: translate("ns1:Seance.TimingAdded"),
        });
        onClose();
      }
    }
  };

  const closeAdvice = () => {
    setHasClosedAdvice(true);
    localStorage.setItem("closed-timing-modal-advice", "true");
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxW="750px" margin="20px" className="!rounded-[30px] p-8">
        <CloseButton
          onClick={onClose}
          alternativeText={translate("ns1:Button.Cancel")}
        />

        {!timingModalAdviceClosed && !hasClosedAdvice && (
          <div className="mx-auto mt-4 flex w-full max-w-[600px] rounded-lg bg-darkGrey-800 p-4 align-top text-white md:mt-12">
            <div className="flex flex-col">
              <span className="text-base font-bold">
                {translate("ns1:Seance.TimeYourSeance")}
              </span>
              <span className="mt-1 text-sm">
                {translate("ns1:Seance.EnterStartTime")}
              </span>
            </div>
            <button className="h-fit" onClick={closeAdvice}>
              <HiOutlineX size={16} />
            </button>
          </div>
        )}

        <div className="mt-6 flex flex-col items-center md:mt-12">
          <h4 className="w-fit text-center font-bold  text-secondaryGrey-900">
            {translate("ns1:Seance.StartSeanceAt")} :
          </h4>
        </div>

        <form onSubmit={handleValidate}>
          <div className="mt-6 flex justify-center md:mt-10">
            <INTimeInput
              defaultValue={timing}
              setFormattedTiming={setFormattedTiming}
            />
          </div>

          <div className="mx-auto mt-4 w-full max-w-[600px] md:mt-12">
            <PrimaryButton
              height={isDesktop ? "52px" : "46px"}
              width="100%"
              disabled={formattedTiming === ""}
              type="submit"
            >
              {translate("ns1:Button.Validate")}
            </PrimaryButton>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
}
